




















































































import {
  defineComponent,
  ref,
  onMounted,
  reactive,
  watch,
} from "@nuxtjs/composition-api";
import useProduct from "~/modules/catalog/product/composables/useProduct";
import { useProductsWithCommonProductCardProps } from "~/modules/catalog/category/components/views/useProductsWithCommonCardProps";
import CategoryProductCard from "~/modules/catalog/category/components/views/CategoryProductCard.vue";
import { Navigation, Pagination, Scrollbar } from "swiper";
import { SwiperCore, Swiper, SwiperSlide } from "swiper-vue2";
SwiperCore.use([Navigation, Pagination, Scrollbar]);
import { useRecommendationProduct } from "~/composables/useRecommendationProduct";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import ProductLoadItem from "~/components/SkeletonLoader/ProductLoadItem.vue";
import { useSearchProducts } from "~/composables";
import { useClevertapStore } from "~/stores/clevertap";

// Define interface for recommendation block
interface RecommendationBlock {
  title: string;
  key: string;
  block_position: number;
  items: string[];
  productsFormatted?: any[];
  loading: boolean;
}

export default defineComponent({
  name: "RelatedProducts",
  components: {
    CategoryProductCard,
    Swiper,
    SwiperSlide,
    SkeletonLoader,
    ProductLoadItem,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { getProductList: getProductListSuggest, loading: loadingSuggest } =
      useProduct(`recommendation`);
    const { fetchProducts, loading: loadingRelatedCategory } = useSearchProducts();
    const clevertapStore = useClevertapStore();

    const slickProductItemsOptions = {
      slidesPerView: 2.05,
      slidesPerGroup: 2,
      spaceBetween: 4,
      centeredSlides: false,
      loop: false,
      pagination: { clickable: true },
      scrollbar: {draggable: true },
      navigation: true,
      freeMode: true,
      breakpoints: {
        640: {
          slidesPerView: 2.05,
          slidesPerGroup: 2,
          spaceBetween: 4,
        },
        768: {
          slidesPerView: 3.2,
          slidesPerGroup: 3,
          spaceBetween: 4,
        },
        1024: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 20,
        },
        1440: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 20,
        },
      },
    };
    const sortedProducts = ref([]);

    const { productsWithCommonProductCardProps: formatProductsForDisplay } =
      useProductsWithCommonProductCardProps(sortedProducts);

    // Create a utility function that can be used inside async functions
    const formatProductsForBlock = (products) => {
      // Update the ref that's bound to useProductsWithCommonProductCardProps
      sortedProducts.value = products;
      // Return the formatted products
      return formatProductsForDisplay.value;
    };

    const { recommendedProducts, getProductRecommendation } =
      useRecommendationProduct();
    const recommendationBlocks = ref<RecommendationBlock[]>([]);
    const allBlocksProcessed = ref(false); // New reactive variable to track processing completion

    // Store processed product SKUs to avoid duplicates across blocks
    const processedProductSkus = ref(new Set());

    // Function to fetch products for a recommendation block
    const fetchRecommendationProducts = async (block: RecommendationBlock) => {
      block.loading = true;

      try {
        // Check if all items in block are null or empty
        const areAllItemsEmpty =
          !block.items ||
          block.items.length === 0 ||
          block.items.every((item) => item === null || item === "");

        if (areAllItemsEmpty && props.product?.related_category_id) {
          // If items are all null/empty and there's a related category, use category search instead

          const params = {
            ids: [String(props.product.related_category_id)],
            pageSize: 12,
          };
          const { data } = await fetchProducts(params);
          const fetchedProducts = data || [];

          if (fetchedProducts) {
            block.productsFormatted = formatProductsForBlock(fetchedProducts);
          }
        } else if (!areAllItemsEmpty) {
          // Filter out items that have already been processed in other blocks
          const uniqueItems = block.items.filter(
            (sku) => !processedProductSkus.value.has(sku)
          );

          // If no unique items left after filtering, skip this block
          if (uniqueItems.length === 0) {
            block.productsFormatted = [];
            block.loading = false;
            return;
          }

          const params = {
            skus: uniqueItems,
            pageSize: uniqueItems.length || 12,
          };
          const { data, total } = await fetchProducts(params);

          const productsData = data || [];

          if (productsData) {
            // Create a Map to store the original position of each SKU
            const itemsOrderMap = new Map();
            uniqueItems.forEach((sku, index) => {
              itemsOrderMap.set(sku, index);
            });

            // Sort products according to the exact order in uniqueItems
            const sortedBlockProducts = [...productsData].sort((a, b) => {
              const aIndex = itemsOrderMap.has(a.sku)
                ? itemsOrderMap.get(a.sku)
                : 999;
              const bIndex = itemsOrderMap.has(b.sku)
                ? itemsOrderMap.get(b.sku)
                : 999;
              return aIndex - bIndex;
            });

            // Use the utility function instead of creating a new instance of useProductsWithCommonProductCardProps
            block.productsFormatted =
              formatProductsForBlock(sortedBlockProducts);

            // Add these product SKUs to our processed set to avoid future duplicates
            sortedBlockProducts.forEach((product) => {
              processedProductSkus.value.add(product.sku);
            });
          }
        }
      } catch (error) {
        console.error(`Error fetching products for ${block.key}:`, error);
      } finally {
        block.loading = false;
      }
    };

    // Process recommendations data and fetch products
    const processRecommendations = async (data: any) => {
      if (!data || !Array.isArray(data)) {
        allBlocksProcessed.value = true; // Set to true if there's no data
        return;
      }

      // Reset processing status and clear processed SKUs
      allBlocksProcessed.value = false;
      processedProductSkus.value.clear();

      // Initialize recommendation blocks
      const blocks = data.map((block) => ({
        ...block,
        loading: false,
        productsFormatted: [],
      }));

      // Sort blocks by position
      blocks.sort((a, b) => a.block_position - b.block_position);
      recommendationBlocks.value = blocks;

      // Fetch products for each block that has items
      for (const block of blocks) {
        if (
          (block.items && block.items.length > 0) ||
          props.product?.related_category_id
        ) {
          await fetchRecommendationProducts(block);
        }
      }

      // Set allBlocksProcessed to true after all blocks have been processed
      allBlocksProcessed.value = true;
    };

    // Wait for CleverTap to be available
    const waitForCleverTap = () => {
      return new Promise<string>((resolve) => {
        const checkCleverTap = () => {
          // Type-safe access with double assertion
          const win = window as any;
          if (
            typeof win !== "undefined" &&
            win.clevertap &&
            typeof win.clevertap.getCleverTapID === "function"
          ) {
            try {
              const id = win.clevertap.getCleverTapID();
              resolve(id || "");
            } catch (err) {
              console.error("Error getting CleverTap ID:", err);
              resolve("");
            }
          } else {
            setTimeout(checkCleverTap, 500);
          }
        };

        checkCleverTap();
      });
    };

    // Function to fetch recommendation data using the current SKU
    const fetchRecommendationData = async () => {
      try {
        const userCode = await waitForCleverTap();
        const internalRefCode = props.product?.sku || "";

        if (
          (userCode && internalRefCode) ||
          props.product?.related_category_id
        ) {
          await getProductRecommendation(userCode, internalRefCode);

          // Process recommendation data when it's available
          if (recommendedProducts.value || props.product?.related_category_id) {
            await processRecommendations(recommendedProducts.value);
          }
        }
      } catch (error) {
        console.error("Error fetching product recommendations:", error);
      }
    };

    // Function to track when a user clicks on a recommended product
    const trackProductRecommendationClick = (product, block, productIndex) => {
      try {
        // Prepare the data for CleverTap tracking
        const fromToData = {
          from_location: "product",
          from_product_id: props.product?.sku || "",
          suggest_product: true,
          suggest_product_title: block.key,
          block_position: block.block_position || 1,
          suggest_product_number: productIndex,
        };
        // Clear any existing data first
        clevertapStore.actionFromTo(null);

        // Then store the new data
        setTimeout(() => {
          clevertapStore.actionFromTo(fromToData);
        }, 100);
      } catch (error) {
        console.error("Error tracking product recommendation click:", error);
      }
    };

    onMounted(async () => {
      await fetchRecommendationData();
    });

    // Watch for changes to product.sku and refetch recommendations when it changes
    watch(
      () => props?.product?.sku,
      async (newSku, oldSku) => {
        if (newSku && newSku !== oldSku) {
          recommendationBlocks.value = [];
          allBlocksProcessed.value = false; // Reset processing status
          await fetchRecommendationData();
        }
      }
    );

    return {
      slickProductItemsOptions,
      productsWithCommonProductCardProps: formatProductsForDisplay,
      recommendedProducts,
      recommendationBlocks,
      allBlocksProcessed, // Export the new variable
      trackProductRecommendationClick,
      loadingSuggest,
      loadingRelatedCategory,
      modules: [Navigation, Scrollbar],
    };
  },
});
